import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Stack} from '@/components/shared/Stack'
import {StatusText} from '@/components/shared/StatusText'
import {MutedText} from '@/components/shared/MutedText'
import {TimelineItem} from '@/components/shared/TimelineItem'
import {formatTimestamp} from '@/lib/date-utils'
import {useI18n} from '@/i18n'
import {RestrictionRuleRequestStatus} from '@/types/restriction-rule-request'
import type {RestrictionRuleRequest} from '@/types/restriction-rule-request'

interface WorkflowTimelineCardProps {
    request: RestrictionRuleRequest
}

type EntryTone = 'success' | 'warning' | 'danger' | 'info'

interface TimelineEntry {
    key: string
    label: string
    timestamp: string | null
    tone: EntryTone
    border: string
    testId: string
}

const BORDER_FOR_TONE: Record<EntryTone, string> = {
    success: 'border-status-success',
    warning: 'border-status-warning',
    danger: 'border-status-danger',
    info: 'border-status-info',
}

/**
 * Sidebar card rendering the lifecycle history of a restriction rule request:
 * Draft → Submitted → Decision (Approved / Rejected / Cancelled). Each entry
 * carries the timestamp the transition was recorded at; entries without a
 * known timestamp render the timestamp slot empty.
 */
export function WorkflowTimelineCard({request}: WorkflowTimelineCardProps) {
    const i18n = useI18n()

    const entries: TimelineEntry[] = []

    entries.push({
        key: 'created',
        label: i18n.restrictionRuleRequests.workflowDraftCreated,
        timestamp: request.createdAt,
        tone: 'info',
        border: BORDER_FOR_TONE.info,
        testId: 'workflow-timeline-created',
    })

    if (request.status !== RestrictionRuleRequestStatus.DRAFT) {
        entries.push({
            key: 'submitted',
            label: i18n.restrictionRuleRequests.workflowSubmittedForApproval,
            // No dedicated submittedAt — we surface updatedAt as the best-known transition time
            // when the request has moved past DRAFT.
            timestamp: request.updatedAt,
            tone: 'warning',
            border: BORDER_FOR_TONE.warning,
            testId: 'workflow-timeline-submitted',
        })
    }

    if (request.status === RestrictionRuleRequestStatus.APPROVED) {
        entries.push({
            key: 'approved',
            label: i18n.restrictionRuleRequests.workflowApproved,
            timestamp: request.reviewedAt,
            tone: 'success',
            border: BORDER_FOR_TONE.success,
            testId: 'workflow-timeline-approved',
        })
    } else if (request.status === RestrictionRuleRequestStatus.REJECTED) {
        entries.push({
            key: 'rejected',
            label: i18n.restrictionRuleRequests.workflowRejected,
            timestamp: request.reviewedAt,
            tone: 'danger',
            border: BORDER_FOR_TONE.danger,
            testId: 'workflow-timeline-rejected',
        })
    } else if (request.status === RestrictionRuleRequestStatus.CANCELLED) {
        entries.push({
            key: 'cancelled',
            label: i18n.restrictionRuleRequests.workflowCancelled,
            timestamp: request.reviewedAt ?? request.updatedAt,
            tone: 'danger',
            border: BORDER_FOR_TONE.danger,
            testId: 'workflow-timeline-cancelled',
        })
    }

    return (
        <Card data-testid="workflow-timeline-card" data-help-prefix="workflowTimeline">
            <CardHeader>
                <CardTitle data-help-key="_section" data-testid="card-title-workflow-timeline">
                    {i18n.restrictionRuleRequests.workflowTitle}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <Stack gap="sm">
                    {entries.map((entry) => (
                        <TimelineItem
                            key={entry.key}
                            borderColorClass={entry.border}
                            data-testid={entry.testId}
                        >
                            <StatusText tone={entry.tone} size="sm">
                                {entry.label}
                            </StatusText>
                            {entry.timestamp && (
                                <div>
                                    <MutedText size="xs">
                                        {formatTimestamp(entry.timestamp)}
                                    </MutedText>
                                </div>
                            )}
                        </TimelineItem>
                    ))}
                </Stack>
            </CardContent>
        </Card>
    )
}
